import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { 
  Truck, MapPin, Package, Calendar, Clock, ChevronRight,
  CheckCircle, AlertCircle, Loader2
} from 'lucide-react';
import { Shipment, SHIPMENT_STATUS_CONFIG } from '../types/shipment';
import { useAuth } from '../contexts/AuthContext';
import { shipmentService } from '../services/shipmentService';
import { hasUserRole } from '../utils/userRoles';
import { isCorporateExternalTransportMobileUser } from '../utils/corporateMobileUser';

export function ActiveJobs() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [activeShipments, setActiveShipments] = useState<Shipment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const canGoToBidMarketplace = !hasUserRole(user, 'corporate-external-transport-driver');
  const isCorporateCarrierUser = hasUserRole(user, 'corporate-external-transport', 'corporate-external-transport-user') || isCorporateExternalTransportMobileUser(user);

  useEffect(() => {
    loadActiveShipments();
  }, [user]);

  const loadActiveShipments = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await shipmentService.getAll();
      const data = response.data || response;
      if (Array.isArray(data)) {
        const companyId = String((user as any)?.externalTransportAccountUserId || (user as any)?.external_transport_account_user_id || user?.id || '');
        const active = data.filter((s: any) => {
          const matchesDriver = isCorporateCarrierUser
            ? String(s.external_transport_account_user_id || '') === companyId
            : [s.driver_user_id, s.assigned_driver_user_id, s.user_id, s.driver_id].some((value) => String(value || '') === String(user?.id || ''));
          return matchesDriver && s.status !== 'delivery-completed' && s.current_stage !== 10;
        }).map((s: any) => ({
          id: s.id?.toString() || s.shipment_id?.toString() || '',
          sevkKodu: s.shipment_no || s.sevk_kodu || s.id,
          driverId: s.driver_id || '',
          driverName: s.driver_name || s.full_name || '',
          vehiclePlate: s.vehicle_plate || '',
          currentStatus: s.status || s.current_status || 'pending',
          loadingCity: s.loading_city || s.origin_city || '',
          loadingPoint: s.loading_point || s.loading_adress || '',
          loadingAddress: s.loading_address || '',
          deliveryCity: s.delivery_city || s.destination_city || '',
          deliveryPoint: s.delivery_point || s.delivery_adress || '',
          deliveryAddress: s.delivery_address || '',
          cargoType: s.cargo_type || '',
          cargoWeight: s.cargo_weight || 0,
          cargoVolume: s.cargo_volume || 0,
          plannedLoadingTime: s.planned_loading_date || s.loading_date || new Date().toISOString(),
          plannedDeliveryTime: s.planned_delivery_date || s.delivery_date || new Date().toISOString(),
          statusHistory: s.status_history || s.statusHistory || [],
          rampAssignment: s.ramp_assignment || s.rampAssignment || null,
        }));
        setActiveShipments(active);
      }
    } catch (err: any) {
      console.error('Failed to load shipments:', err);
      setError('Sevkiyatlar yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const getProgressPercentage = (shipment: Shipment) => {
    return (shipment.statusHistory?.length || 0) / 11 * 100;
  };

  if (loading) {
    if (isCorporateCarrierUser) {
      return (
        <div className="min-h-screen bg-slate-50 pb-28 text-slate-900">
          <div className="rounded-b-[2rem] bg-slate-950 px-5 pb-8 pt-[max(env(safe-area-inset-top),1.25rem)] text-white shadow-xl">
            <div className="text-xs font-bold uppercase tracking-[0.22em] text-orange-300">Kurumsal Sevkiyatlar</div>
            <h1 className="mt-3 text-3xl font-black">İşler</h1>
          </div>
          <div className="flex items-center justify-center py-16 text-slate-500"><Loader2 className="mr-2 h-5 w-5 animate-spin" /> Sevkiyatlar yükleniyor...</div>
        </div>
      );
    }
    return <div className="flex min-h-[60vh] items-center justify-center text-gray-600"><Loader2 className="mr-2 h-6 w-6 animate-spin text-blue-600" /> Sevkiyatlar yükleniyor...</div>;
  }

  if (activeShipments.length === 0) {
    if (isCorporateCarrierUser) {
      return (
        <div className="min-h-screen bg-slate-50 pb-28 text-slate-900">
          <div className="rounded-b-[2rem] bg-slate-950 px-5 pb-8 pt-[max(env(safe-area-inset-top),1.25rem)] text-white shadow-xl">
            <div className="text-xs font-bold uppercase tracking-[0.22em] text-orange-300">Kurumsal Sevkiyatlar</div>
            <h1 className="mt-3 text-3xl font-black">İşler</h1>
            <p className="mt-2 text-sm text-slate-300">Firmanıza atanmış aktif sevkiyatları buradan takip edin.</p>
          </div>
          <div className="px-4 py-6">
            <div className="rounded-3xl border border-dashed border-slate-300 bg-white px-5 py-14 text-center shadow-sm">
              <Truck className="mx-auto mb-4 h-14 w-14 text-slate-300" />
              <h3 className="text-lg font-black text-slate-900">Aktif iş bulunamadı</h3>
              <p className="mt-2 text-sm leading-6 text-slate-500">Şu anda firmanıza bağlı devam eden sevkiyat yok.</p>
              {canGoToBidMarketplace ? (
                <button onClick={() => navigate('/bid-marketplace')} className="mt-6 rounded-2xl bg-slate-950 px-5 py-3 text-sm font-bold text-white">
                  Teklif Taleplerine Git
                </button>
              ) : null}
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Aktif İşlerim</h1>
          <p className="text-gray-600 mt-1">Devam eden sevkiyatlarınız</p>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
          <Truck className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Aktif İş Bulunamadı</h3>
          <p className="text-gray-600 mb-6">
            Şu anda devam eden sevkiyatınız bulunmuyor.
          </p>
          {canGoToBidMarketplace ? (
            <button
              onClick={() => navigate('/bid-marketplace')}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors"
            >
              Teklif Pazarına Git
            </button>
          ) : null}
        </div>
      </div>
    );
  }

  return (
    <div className={isCorporateCarrierUser ? 'min-h-screen bg-slate-50 pb-28 text-slate-900' : 'space-y-6'}>
      {/* Header */}
      {isCorporateCarrierUser ? (
        <div className="rounded-b-[2rem] bg-slate-950 px-5 pb-8 pt-[max(env(safe-area-inset-top),1.25rem)] text-white shadow-xl">
          <div className="text-xs font-bold uppercase tracking-[0.22em] text-orange-300">Kurumsal Sevkiyatlar</div>
          <h1 className="mt-3 text-3xl font-black">İşler</h1>
          <p className="mt-2 text-sm text-slate-300">Devam eden {activeShipments.length} sevkiyatınız var.</p>
        </div>
      ) : (
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Aktif İşlerim</h1>
          <p className="text-gray-600 mt-1">
            Devam eden {activeShipments.length} sevkiyatınız var
          </p>
        </div>
      )}

      {/* Active Shipments */}
      <div className={isCorporateCarrierUser ? 'grid grid-cols-1 gap-4 px-4 py-5 lg:grid-cols-2' : 'grid grid-cols-1 lg:grid-cols-2 gap-6'}>
        {activeShipments.map(shipment => {
          const currentConfig = SHIPMENT_STATUS_CONFIG[shipment.currentStatus] || {
            label: 'Bilinmiyor',
            color: '#6b7280',
            bgColor: '#f3f4f6',
            description: 'Durum bilinmiyor'
          };
          const progressPercentage = getProgressPercentage(shipment);

          return (
            <div 
              key={shipment.id} 
              className={isCorporateCarrierUser ? 'overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm transition hover:shadow-md' : 'bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow'}
            >
              {/* Header */}
              <div className={isCorporateCarrierUser ? 'bg-slate-950 p-4 text-white' : 'bg-gradient-to-r from-blue-500 to-blue-600 p-4 text-white'}>
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-lg font-bold">{shipment.sevkKodu}</h3>
                  <span
                    className="px-3 py-1 rounded-full text-xs font-semibold"
                    style={{ backgroundColor: currentConfig.bgColor, color: currentConfig.color }}
                  >
                    {currentConfig.label}
                  </span>
                </div>
                <p className="text-sm text-slate-300">{shipment.vehiclePlate || 'Plaka atanmadı'}</p>
              </div>

              <div className="p-4 space-y-4">
                {/* Progress */}
                <div>
                  <div className="flex items-center justify-between text-sm mb-2">
                    <span className="text-gray-600">İlerleme</span>
                    <span className="font-semibold text-gray-900">
                      {Math.round(progressPercentage)}%
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-blue-600 h-2 rounded-full transition-all duration-500"
                      style={{ width: `${progressPercentage}%` }}
                    />
                  </div>
                </div>

                {/* Route */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-blue-50 rounded-lg p-3">
                    <p className="text-xs text-blue-600 font-medium mb-1">Yükleme</p>
                    <p className="font-semibold text-blue-900 text-sm">{shipment.loadingCity}</p>
                    <p className="text-xs text-blue-700 mt-0.5">{shipment.loadingPoint}</p>
                  </div>
                  <div className="bg-green-50 rounded-lg p-3">
                    <p className="text-xs text-green-600 font-medium mb-1">Teslimat</p>
                    <p className="font-semibold text-green-900 text-sm">{shipment.deliveryCity}</p>
                    <p className="text-xs text-green-700 mt-0.5">{shipment.deliveryPoint}</p>
                  </div>
                </div>

                {/* Cargo Info */}
                <div className="bg-gray-50 rounded-lg p-3">
                  <div className="flex items-start gap-2">
                    <Package className="w-4 h-4 text-gray-600 mt-0.5" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-900">{shipment.cargoType}</p>
                      <div className="flex gap-3 mt-1 text-xs text-gray-600">
                        <span>{shipment.cargoWeight.toLocaleString()} kg</span>
                        <span>{shipment.cargoVolume} m³</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Dates */}
                <div className="grid grid-cols-2 gap-3 text-xs">
                  <div>
                    <p className="text-gray-600 mb-1 flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      Yükleme
                    </p>
                    <p className="font-medium text-gray-900">
                      {new Date(shipment.plannedLoadingTime).toLocaleDateString('tr-TR', {
                        day: 'numeric',
                        month: 'short',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-600 mb-1 flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      Teslimat
                    </p>
                    <p className="font-medium text-gray-900">
                      {new Date(shipment.plannedDeliveryTime).toLocaleDateString('tr-TR', {
                        day: 'numeric',
                        month: 'short',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </p>
                  </div>
                </div>

                {/* Current Status */}
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="w-4 h-4 text-yellow-600 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-yellow-900">{currentConfig.label}</p>
                      <p className="text-xs text-yellow-700 mt-0.5">{currentConfig.description}</p>
                    </div>
                  </div>
                </div>

                {/* Rampa Assignment */}
                {shipment.rampAssignment && (
                  <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-purple-600" />
                      <div>
                        <p className="text-sm font-medium text-purple-900">Rampa Atandı</p>
                        <p className="text-xs text-purple-700 mt-0.5">
                          {shipment.rampAssignment.rampName}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Action Button */}
                <button
                  onClick={() => navigate('/driver-tracking')}
                  className={isCorporateCarrierUser ? 'flex w-full items-center justify-center gap-2 rounded-2xl bg-slate-950 py-3 font-bold text-white transition-colors' : 'w-full bg-blue-600 text-white py-3 rounded-lg font-medium hover:bg-blue-700 transition-colors flex items-center justify-center gap-2'}
                >
                  Sevkiyatı Takip Et
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
